import type { CSSProperties } from "react";

/**
 * Falsterbo palette — single source of truth for inline styles.
 *
 * Every colour resolves to a CSS custom property (defined in index.css for both
 * light and `prefers-color-scheme: dark`). Because inline styles read the var at
 * paint time, the whole UI re-themes for dark mode with zero per-component work.
 * Never hardcode a hex in a component — add a token here + in index.css instead.
 */
export const C = {
  // text
  ink: "var(--ink)",
  textMid: "var(--text-mid)",
  textLow: "var(--text-low)",
  muted: "var(--muted)", // low-emphasis text (meta, captions) — AA on the paper surface
  mutedDeep: "var(--muted-deep)",
  // accents (each has a -deep variant that stays legible as text on a tint)
  sea: "var(--sea)",
  seaDeep: "var(--sea-deep)",
  ice: "var(--ice)",
  sage: "var(--sage)",
  sageDeep: "var(--sage-deep)",
  beacon: "var(--beacon)",
  beaconDeep: "var(--beacon-deep)",
  coral: "var(--coral)",
  coralDeep: "var(--coral-deep)",
  rain: "var(--rain)", // rainfall blue on the balance chart
  // surfaces & lines
  paper: "var(--paper)",
  raise: "var(--raise)", // slightly raised surface (hero gradient end, hover fill)
  line: "var(--line)",
  lineSoft: "var(--line-soft)",
  hairline: "var(--hairline)", // secondary divider / control border
  hoverBorder: "var(--hover-border)",
  track: "var(--track)", // progress / gauge track
  sand: "var(--sand)",
  onAccent: "var(--on-accent)", // text/icon on a saturated accent fill
} as const;

export const FONT = {
  serif: "var(--font-serif)",
  sans: "var(--font-sans)",
  mono: "var(--font-mono)",
} as const;

/** 4px-based spacing scale. Use these instead of ad-hoc gap/padding/margin. */
export const S = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  7: 32,
  8: 40,
} as const;

/** Type-size ramp (px). Replaces scattered half-pixel font sizes. */
export const T = {
  xs: 11,
  sm: 12,
  base: 14,
  md: 17,
  lg: 18,
  xl: 22,
  d1: 26,
  d2: 33,
  d3: 40,
} as const;

export const RADIUS = {
  card: "var(--radius-card)",
  cardLg: "var(--radius-card-lg)",
  pill: "var(--radius-pill)",
  btn: "var(--radius-btn)",
  chip: "var(--radius-chip)",
} as const;

/** Standard card surface — tokens, so it re-themes for dark mode. */
export const card: CSSProperties = {
  background: C.paper,
  border: `1px solid ${C.line}`,
  borderRadius: "var(--radius-card)",
  boxShadow: "var(--shadow-card)",
};

export const cardHoverShadow = "var(--shadow-card-hover)";

export const clamp = (n: number, a: number, b: number) => Math.max(a, Math.min(b, n));

/**
 * Translucent version of any colour token (hex or `var(--x)`), via color-mix so
 * it works on CSS variables and survives dark-mode swaps. `a` is 0..1 opacity.
 */
export const hexA = (color: string, a: number) =>
  `color-mix(in srgb, ${color} ${Math.round(clamp(a, 0, 1) * 100)}%, transparent)`;

/** Alias kept for readability at call sites that mean "this colour, faded". */
export const withAlpha = hexA;

export const mmss = (s: number) =>
  `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export const commas = (n: number) => n.toLocaleString("en-US");
